import { useEffect, useState } from "react";
import API from "../../../services/api";
import "./SchoolGallery.css";

export default function SchoolGallery() {
  const schoolAdmin = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);

  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [folderName, setFolderName] = useState("");

  const [showUpload, setShowUpload] = useState(false);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [loading, setLoading] = useState(false);

  // =========================
  // LOAD GALLERY
  // =========================
  const loadGallery = async (keepFolderId = null) => {
    try {
      const res = await API.post(
        "/school/gallery/",
        {},
        {
          headers: {
            "X-SCHOOL-ID": schoolAdmin.school.id,
          },
        },
      );

      const data = res.data.folders || [];

      setFolders(data);

      if (keepFolderId) {
        const updated = data.find((f) => f.id === keepFolderId);
        setActiveFolder(updated || null);
      }
    } catch (err) {
      console.error("Gallery load failed", err);
    }
  };

  useEffect(() => {
    loadGallery();
  }, []);

  // =========================
  // CREATE FOLDER
  // =========================
  const createFolder = async () => {
    if (!folderName.trim()) {
      alert("Folder name required");
      return;
    }

    try {
      await API.post(
        "/school-admin/gallery/folder/create/",
        { name: folderName },
        {
          headers: {
            "X-SCHOOL-ID": schoolAdmin.school.id,
          },
        },
      );

      setFolderName("");
      setShowFolderForm(false);

      loadGallery();
    } catch {
      alert("Failed to create folder");
    }
  };

  // =========================
  // UPLOAD FILE
  // =========================
  const uploadImage = async () => {
    if (!file || !activeFolder) {
      alert("Select folder & file");
      return;
    }

    const form = new FormData();

    // ✅ IMPORTANT
    form.append("file", file);

    form.append("caption", caption);
    form.append("folder", activeFolder.id);

    setLoading(true);

    try {
      await API.post("/school-admin/gallery/image/upload/", form, {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-SCHOOL-ID": schoolAdmin.school.id,
        },
      });

      setFile(null);
      setCaption("");
      setShowUpload(false);

      loadGallery(activeFolder.id);
    } catch (err) {
      console.error(err);
      alert("File upload failed");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // DELETE FILE
  // =========================
  const deleteImage = async (imageId) => {
    if (!window.confirm("Delete this file permanently?")) return;

    try {
      await API.post(
        "/school-admin/gallery/image/delete/",
        { image_id: imageId },
        {
          headers: {
            "X-SCHOOL-ID": schoolAdmin.school.id,
          },
        },
      );

      loadGallery(activeFolder.id);
    } catch {
      alert("Failed to delete file");
    }
  };

  // =========================
  // FILE TYPE HELPERS
  // =========================
  const isPdf = (url) => url?.toLowerCase().includes(".pdf");

  const isExcel = (url) =>
    url?.toLowerCase().includes(".xls") || url?.toLowerCase().includes(".xlsx");

  const isWord = (url) =>
    url?.toLowerCase().includes(".doc") || url?.toLowerCase().includes(".docx");

  const isPpt = (url) =>
    url?.toLowerCase().includes(".ppt") || url?.toLowerCase().includes(".pptx");

  const isImage = (url) => url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

  const totalFiles = folders.reduce(
    (total, folder) => total + (folder.images?.length || 0),
    0,
  );

  const activeFileCount = activeFolder?.images?.length || 0;

  return (
    <div className="gallery-page">
      {/* HEADER */}
      <div className="gallery-hero">
        <div className="gallery-hero-left">
          <span className="gallery-hero-icon">📄</span>
          <div>
            <h1>Question Papers</h1>
            <p>Organize folders and upload question paper files for students.</p>
          </div>
        </div>

        <div className="gallery-actions">
          <button
            className="btn-add"
            onClick={() => setShowFolderForm(!showFolderForm)}
          >
            + New Folder
          </button>

          {activeFolder && (
            <button
              className="btn-add"
              onClick={() => setShowUpload(!showUpload)}
            >
              + Upload Paper
            </button>
          )}
        </div>
      </div>

      <div className="gallery-stats">
        <div className="gallery-stat-card">
          <span>Folders</span>
          <strong>{folders.length}</strong>
        </div>
        <div className="gallery-stat-card">
          <span>Total Files</span>
          <strong>{totalFiles}</strong>
        </div>
        <div className="gallery-stat-card">
          <span>Current Folder</span>
          <strong>{activeFolder ? activeFileCount : "-"}</strong>
        </div>
      </div>

      {/* CREATE FOLDER */}
      {showFolderForm && (
        <div className="gallery-form">
          <input
            type="text"
            placeholder="Folder / Subject Name"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
          />

          <button onClick={createFolder}>Create Folder</button>
        </div>
      )}

      {/* UPLOAD */}
      {showUpload && activeFolder && (
        <div className="gallery-form">
          <p>
            Uploading to:
            <strong> {activeFolder.name}</strong>
          </p>

          <input
            type="file"
            accept="
              .pdf,
              .doc,
              .docx,
              .xls,
              .xlsx,
              .ppt,
              .pptx,
              .jpg,
              .jpeg,
              .png
            "
            onChange={(e) => setFile(e.target.files[0])}
          />

          <input
            type="text"
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />

          <button onClick={uploadImage} disabled={loading}>
            {loading ? "Uploading..." : "Upload File"}
          </button>
        </div>
      )}

      {/* =========================
          FOLDER LIST
      ========================= */}
      {!activeFolder && (
        <>
        <div className="gallery-section-header">
          <div>
            <h2>Folders</h2>
            <p>{folders.length} folder{folders.length === 1 ? "" : "s"} available</p>
          </div>
        </div>
        <div className="folder-grid">
          {folders.length === 0 && (
            <div className="gallery-empty">
              <span>📁</span>
              <p>No folders created yet</p>
              <small>Create a folder to group question papers by subject, class, or exam.</small>
            </div>
          )}

          {folders.map((f) => (
            <div
              key={f.id}
              className="folder-card"
              onClick={() => setActiveFolder(f)}
            >
              <span className="folder-icon">📁</span>
              <p>{f.name}</p>
              <small>{f.images.length} files</small>
            </div>
          ))}
        </div>
        </>
      )}

      {/* =========================
          FILE GRID
      ========================= */}
      {activeFolder && (
        <>
          <div className="gallery-section-header active-folder-header">
            <div>
              <h2>{activeFolder.name}</h2>
              <p>{activeFileCount} file{activeFileCount === 1 ? "" : "s"} in this folder</p>
            </div>
            <button className="btn-back" onClick={() => setActiveFolder(null)}>
              Back to folders
            </button>
          </div>

          <div className="gallery-grid">
            {activeFolder.images?.length === 0 && (
              <div className="gallery-empty">
                <span>📄</span>
                <p>No files in this folder</p>
                <small>Use Upload Paper to add PDFs, documents, spreadsheets, or images.</small>
              </div>
            )}
            {activeFolder.images?.map((img) => (
              <div key={img.id} className="premium-file-card">
                {/* TOP PREVIEW */}
                <div className="premium-preview">
                  {/* IMAGE */}
                  {isImage(img.image) && (
                    <img
                      src={img.image}
                      alt={img.caption}
                      className="premium-image"
                    />
                  )}

                  {/* PDF */}
                  {isPdf(img.image) && (
                    <div className="premium-file-icon pdf-bg">
                      <div className="file-emoji">📕</div>
                      <h4>PDF Document</h4>
                      <span>Portable Document File</span>
                    </div>
                  )}

                  {/* EXCEL */}
                  {isExcel(img.image) && (
                    <div className="premium-file-icon excel-bg">
                      <div className="file-emoji">📗</div>
                      <h4>Excel Sheet</h4>
                      <span>XLS / XLSX File</span>
                    </div>
                  )}

                  {/* WORD */}
                  {isWord(img.image) && (
                    <div className="premium-file-icon word-bg">
                      <div className="file-emoji">📘</div>
                      <h4>Word Document</h4>
                      <span>DOC / DOCX File</span>
                    </div>
                  )}

                  {/* PPT */}
                  {isPpt(img.image) && (
                    <div className="premium-file-icon ppt-bg">
                      <div className="file-emoji">📙</div>
                      <h4>Presentation</h4>
                      <span>PPT / PPTX File</span>
                    </div>
                  )}
                </div>

                {/* CONTENT */}
                <div className="premium-file-content">
                  <h3 className="premium-file-title">
                    {img.caption || "Untitled File"}
                  </h3>

                  <p className="premium-file-subtitle">
                    Uploaded Question Paper
                  </p>

                  {/* ACTIONS */}
                  <div className="premium-actions">
                    <a
                      href={img.image}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-premium-view"
                    >
                      View File
                    </a>

                    <button
                      className="btn-premium-delete"
                      onClick={() => deleteImage(img.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
